import React, {useState} from 'react';
import {filter, includes, noop, range} from 'lodash';
import InfoTooltip from 'shared/components/info-tooltip';
import Label from 'shared/components/label';
import TextButton from 'src/components/TextButton';
import AccessibleTypeahead from 'shared/components/accessible-typeahead';
import InstitutionTypeahead from 'shared/institution-typeahead/components/institution-typeahead';
import UnsavedChangesModal from 'src/components/unsaved-changes-modal';
import Icon from 'shared/components/icon';
import FormCard from 'shared/portfolio/components/form-card';
import PortfolioFormFooter from 'src/components/PortfolioFormFooter';
import {possibleMajorsMap as possibleMajors} from 'transfer-student/utils';
import {validate} from './utils.js';
import {useDispatch, useSelector} from 'react-redux';
import {saveAboutMe} from 'shared/about-me/actions/about-me';
import {Formik} from 'formik';
import {Store} from 'src/types/store';
import FormikDropdown from 'src/components/formik-dropdown';
import FormikRadioButtons from 'src/components/formik-radio-buttons';
import FormikTextArea from 'src/components/formik-text-area';

const currentYear = new Date().getFullYear();

const years = range(currentYear, currentYear + 5, 1).map((year) => {
  const yearStr = year.toString();
  return {value: yearStr, text: yearStr};
});

const semesters = ['Fall', 'Spring', 'Summer'].map((semester) => ({
  value: semester,
  text: semester,
}));

interface TransferTerm {
  term: string;
  year: number;
  transferTerms: [];
}

interface Props {
  name: string;
  followedCollegesTransferTerms: TransferTerm[];
  onCancel: () => void;
  followedCollegeIds: string[];
  openCollegesNotOfferingTransferTermModal: (term) => void;
}

const AboutMeForm = ({
  name,
  onCancel,
  followedCollegesTransferTerms,
  followedCollegeIds,
  openCollegesNotOfferingTransferTermModal,
}: Props) => {
  const [dismissedNoTermsOfferedNotice, setDismissedNoTermsOfferedNotice] = useState(false);
  const dispatch = useDispatch();
  const student = useSelector((state: Store) => state.student);
  const currentInstitution = student.currentInstitution;

  const notOfferedCollegeTransferTerms = filter(followedCollegesTransferTerms, (college) => {
    const desiredTransferTerm = student?.communityCollegeInfo?.transferSemester?.toLowerCase();
    return !includes(college.transferTerms, desiredTransferTerm);
  }).length;

  return (
    <FormCard id="transfer-about-me">
      <Formik
        initialValues={{
          _id: student._id,
          communityCollegeInfo: {
            transferYear: student?.communityCollegeInfo?.transferYear,
            transferSemester: student?.communityCollegeInfo?.transferSemester,
            intendedMajor: student?.communityCollegeInfo?.intendedMajor || '',
            plansToCompleteAssociateDegree: `${student?.communityCollegeInfo?.plansToCompleteAssociateDegree}`,
          },
          currentInstitution: {
            country: currentInstitution.country,
            city: currentInstitution.city,
            subdivision: currentInstitution.subdivision,
            parentId: currentInstitution.parentId || '',
            name: currentInstitution.name || '',
            type: currentInstitution.type || '',
            campus: currentInstitution.campus,
            campuses:
              currentInstitution.campuses?.map((campus) => ({
                _id: campus._id,
                name: campus.name,
                label: campus.name,
              })) || [],
          },
          bio: student.bio || '',
        }}
        validate={validate}
        onSubmit={(values) => {
          dispatch(saveAboutMe(values)).then(onCancel);
        }}
      >
        {({handleSubmit, errors, values, setFieldValue}) => (
          <>
            <UnsavedChangesModal />
            <div className="about-me-form" data-testid="about-me-form">
              <form onSubmit={handleSubmit} autoComplete="off">
                <h1 className="about-me-form-header">{name}</h1>
                <div className="about-me-form-side-by-side-grid">
                  <Label required>
                    Intended transfer year
                    <FormikDropdown
                      testid="transfer-year"
                      name="communityCollegeInfo.transferYear"
                      options={years}
                    />
                  </Label>
                  <Label required>
                    Intended transfer term
                    <FormikDropdown
                      name="communityCollegeInfo.transferSemester"
                      options={semesters}
                    />
                  </Label>
                </div>
                {notOfferedCollegeTransferTerms > 0 && !dismissedNoTermsOfferedNotice && (
                  <div className="deferred-transfer-warning">
                    <Icon iconType="info-circle" className="deferred-warning" />
                    {notOfferedCollegeTransferTerms} of your {followedCollegeIds.length} followed
                    colleges do not offer this transfer term. The Earnings Deadlines for the next
                    available transfer term will apply to these colleges.
                    <TextButton
                      onClick={() =>
                        openCollegesNotOfferingTransferTermModal(
                          values?.communityCollegeInfo?.transferSemester
                        )
                      }
                      margin={false}
                      className="view-colleges"
                    >
                      View colleges
                    </TextButton>
                    <TextButton
                      margin={false}
                      className="close-deferred-transfer-warning"
                      color="dark-gray"
                      onClick={() => setDismissedNoTermsOfferedNotice(true)}
                    >
                      <Icon iconType="times-r" />
                    </TextButton>
                  </div>
                )}
                <div id="transfer-about-me-intended-major-sec">
                  <Label required>
                    Intended Major
                    <AccessibleTypeahead
                      id="community-college-about-me-major-typeahead"
                      value={values.communityCollegeInfo.intendedMajor}
                      options={possibleMajors}
                      onSelect={({value}) => {
                        setFieldValue('communityCollegeInfo.intendedMajor', value);
                      }}
                      onInputChange={(value) => {
                        setFieldValue('communityCollegeInfo.intendedMajor', value || '');
                      }}
                      error={errors?.communityCollegeInfo?.intendedMajor}
                      placeholder="e.g. History"
                      labelKey="label"
                    />
                  </Label>
                </div>
                <div className="mt-20">
                  <InstitutionTypeahead
                    institutionType="community-college"
                    renderLabel={() => 'Current College'}
                    showCampus
                  />
                </div>
                <FormikRadioButtons
                  name="communityCollegeInfo.plansToCompleteAssociateDegree"
                  options={[
                    {value: 'true', label: 'Yes'},
                    {value: 'false', label: 'No / not sure / does not apply to me'},
                  ]}
                  className="mt-20"
                  legendText={
                    <span>
                      Do you plan to complete an Associate degree before transferring?
                      <InfoTooltip>
                        Some colleges award transfer scholarships for credit hour progress to
                        students who plan to complete an Associate degree before transferring
                      </InfoTooltip>
                    </span>
                  }
                />
                <Label>
                  Bio
                  <FormikTextArea
                    name="bio"
                    placeholder="Enter a brief description about yourself."
                  />
                </Label>
                <PortfolioFormFooter
                  hideDelete
                  _id="about-me-footer"
                  onDelete={noop}
                  onCancel={onCancel}
                  formName="about-me about-me-form"
                />
              </form>
            </div>
          </>
        )}
      </Formik>
    </FormCard>
  );
};

export default AboutMeForm;
